<template>
  <section class="inner-wrapper confirmation">
    <div class="left">
      <h2 class="confirmation__header">
        <strong>Thank You! We're Processing Your Order.</strong>
      </h2>
      <p class="mb-1">
        <strong>We'll send you a confirmation email at {{customerEmail}}.</strong>
      </p>
      <p class="mb-1">
        <strong>Please make sure to thoroughly read your rental agreement and our tips on avoiding dumpster rental fees.</strong>
      </p>
      <p class="mb-1">A member of our team will reach out if we have any questions about your placement location or permits.</p>
      <p class="mb-1">In the meantime, check out the resources below to prepare for your dumpster rental.</p>

      <h3 class="confirmation__header--small mt-2">How to Prep for a Dumpster Delivery</h3>
      <p class="mb-1">
        Once you've scheduled your dumpster rental, you'll need to prepare the drop-off location to ensure a successful delivery. Watch the video and check out the tips below to get ready for your container's arrival.
      </p>
      <div v-html="servicePageVideo"/>
      <a class="btn btn--secondary mt-3 mb-2" v-if="isBrandBD" href="https://www.budgetdumpster.com/resources/dumpster-delivery-tips.php">
        More Delivery Tips &amp; Guidelines
      </a>
    </div>
    <div class="right-column">
      <div class="right-column__sticky">
        <div class="summary-wrapper summary-wrapper--confirmation">
          <OrderSummary :hideEdit="true" />
        </div>
      </div>
    </div>
  </section>

  <section class="avoid-fees">
    <h3 class="avoid-fees__header">How to Avoid Extra Fees</h3>
    <div class="avoid-fees__description-wrapper">
      <div class="avoid-fees__description">
        <div class="avoid-fees__icon-wrapper">
          <img :src="calendarIcon"/>
        </div>
        <p class="pt-1">
          Schedule your pickup online before the end of your rental period. If your requested pickup date falls after the last day of your rental period, there will be an extension fee for each additional day your dumpster is on-site.
        </p>
      </div>
      <div class="avoid-fees__description">
        <div class="avoid-fees__icon-wrapper">
          <img :src="weightIcon"/>
        </div>
        <p class="pt-1">
          Pay attention to your weight limit. The landfill will charge an additional disposal fee to cover the difference if the weight of your debris exceeds the set weight limit for your dumpster rental. Use our
          <a :href="weightCalculator">weight calculator</a> to estimate the tonnage of your materials ahead of time.
        </p>
      </div>
      <div class="avoid-fees__description">
        <div class="avoid-fees__icon-wrapper">
          <img :src="warningIcon"/>
        </div>
        <p class="pt-1">
          Notify us ASAP if your plans change. You will be charged a service fee if you cancel or change your service request after 3 p.m. on the business day before your scheduled delivery or pickup date.
        </p>
      </div>
    </div>
    <p class="avoid-fees__disclaimer">
      Find more information on how to avoid <a :href="avoidDumpsterPickupFee">common dumpster rental fees</a>.
    </p>
  </section>

  <section class="dumpster-pickup" v-if="isBrandBD">
    <h3 class="dumpster-pickup__header">Don't Forget to Schedule Your Dumpster Pickup</h3>
    <p class="pb-1">Watch the video below to learn more about <a href="https://www.budgetdumpster.com/schedule-a-pickup.php#how-to-prepare">how to prepare for pickup</a> once your project is completed.</p>
    <div class="dumpster-pickup__video mb-2" v-html="avoidDumpsterPickupFeeVideo"/>
  </section>

  <section class="schedule-pickup" v-if="!isBrandBD">
    <div class="schedule-pickup__wrapper">
      <div class="schedule-pickup__copy">
        <h2>Don't Forget to Schedule Your Dumpster Pickup</h2>
        <p>Follow these tips to prepare for pickup once your project is complete:</p>
        <ul>
          <li class="mb-1">Be on the lookout for a personalized link from us to request a pickup date for your dumpster. Remember this date should fall on or before the last day of your rental agreement to avoid additional fees.</li>
          <li class="mb-1">Confirm that debris doesn’t stick out over the top or sides of the dumpster.</li>
          <li class="mb-1">Make sure the dumpster door is fully closed and latched.</li>
          <li class="mb-1">Keep the area around the dumpster clear and accessible.</li>
        </ul>
      </div>
      <img class="schedule-pickup__image" :src="schedulePickupImage"/>
    </div>
  </section>

  <LightFooter/>
</template>

<script>
import config from '@/config'
import OrderSummary from '@/components/OrderSummary.vue'
import LightFooter from '@/components/LightFooter'
import { timestamp, isGoogleEventsReady, getClientID } from '@/utilities.js'

const { cdn, servicePageVideo, links, avoidDumpsterPickupFeeVideo, phoneNumber } = config
const { weightCalculator, avoidDumpsterPickupFee } = links
export default {
  name: 'Confirmation',
  components: {
    OrderSummary,
    LightFooter
  },
  beforeRouteEnter (to, from, next) {
    if (to.name === 'Confirmation' && from.name !== 'Payment') {
      next('/')
    } else {
      next()
    }
  },
  data () {
    return {
      calendarIcon: `${cdn}calendar.svg`,
      weightIcon: `${cdn}weight.svg`,
      warningIcon: `${cdn}warning.svg`,
      schedulePickupImage: `${cdn}get-more-value-test3.webp`,
      servicePageVideo,
      weightCalculator,
      avoidDumpsterPickupFee,
      avoidDumpsterPickupFeeVideo,
      phoneNumber
    }
  },
  computed: {
    isBrandBD () {
      return process.env.VUE_APP_BRAND === 'BD'
    },
    customerEmail () {
      return this.$store.state.customer.email || 'test@test.com' // TODO: remove this later
    }
  },
  mounted () {
    if (isGoogleEventsReady()) {
      const time = timestamp()
      window.gtag('event', 'generate_lead')
      window.gtag('event', 'Ecommerce - PageView', {
        event_category: 'Ecommerce',
        event_label: `${time}~${window.location.href}`,
        send_to: process.env.VUE_APP_UA_ID
      })

      window.gtag('event', 'Ecommerce - PageView', {
        event_category: 'Ecommerce',
        timestamp: time,
        page: 'confirmation',
        bd_client_id: getClientID(),
        send_to: process.env.VUE_APP_GA4_ID
      })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
